import {
  Array,
  Boolean,
  Dictionary,
  Literal,
  Null,
  Number,
  Record as RRecord,
  Static,
  String,
  Union,
  Unknown,
} from "runtypes";

import { getNormalEnum } from "../runtypeEnums.js";

import { ToolNameLiteral } from "./agent/toolNames.js";

export const AnthropicModelLiteral = Union(
  Literal("CLAUDE_SONNET_35"),
  Literal("CLAUDE_3_OPUS"),
  Literal("RESEARCH_MODEL_FIREFLY"),
  Literal("CLAUDE_SONNET_35_NEW"),
  Literal("CLAUDE_SONNET_37"),
  Literal("CLAUDE_4_OPUS"),
  Literal("CLAUDE_4_SONNET"),
  Literal("CLAUDE_4_5_SONNET"),
  Literal("CLAUDE_4_5_HAIKU"),
  Literal("CLAUDE_4_5_OPUS"),
  Literal("CLAUDE_4_6_OPUS"),
  Literal("CLAUDE_4_7_OPUS"),
  Literal("CLAUDE_NOUGAT_EAP"),
  Literal("CLAUDE_FUDGE_EAP"),
  Literal("CLAUDE_CHURRO_EAP"),
  Literal("CLAUDE_4_6_SONNET"),
);
export type AnthropicModel = Static<typeof AnthropicModelLiteral>;
export const AnthropicModel = getNormalEnum(AnthropicModelLiteral);

export const AnthropicModelNameMap: Record<AnthropicModel, string> = {
  [AnthropicModel.CLAUDE_SONNET_35]: "claude-3-5-sonnet-20240620",
  [AnthropicModel.CLAUDE_3_OPUS]: "claude-3-opus-20240229",
  [AnthropicModel.RESEARCH_MODEL_FIREFLY]: "research-model-firefly-v2",
  [AnthropicModel.CLAUDE_SONNET_35_NEW]: "claude-3-5-sonnet-20241022",
  [AnthropicModel.CLAUDE_SONNET_37]: "claude-3-7-sonnet-20250219",
  [AnthropicModel.CLAUDE_4_SONNET]: "claude-4-sonnet-20250514",
  [AnthropicModel.CLAUDE_4_OPUS]: "claude-4-opus-20250514",
  [AnthropicModel.CLAUDE_4_5_SONNET]: "claude-sonnet-4-5-20250929",
  [AnthropicModel.CLAUDE_4_5_HAIKU]: "claude-haiku-4-5-20251001",
  [AnthropicModel.CLAUDE_4_5_OPUS]: "claude-opus-4-5-20251101",
  [AnthropicModel.CLAUDE_4_6_OPUS]: "claude-opus-4-6",
  [AnthropicModel.CLAUDE_4_7_OPUS]: "claude-opus-4-7",
  [AnthropicModel.CLAUDE_NOUGAT_EAP]: "claude-nougat-eap",
  [AnthropicModel.CLAUDE_FUDGE_EAP]: "claude-fudge-eap",
  [AnthropicModel.CLAUDE_CHURRO_EAP]: "claude-churro-eap",
  [AnthropicModel.CLAUDE_4_6_SONNET]: "claude-sonnet-4-6",
};

/**
 * Supported models for Anthropic server-side compaction.
 */
export function supportsAnthropicServerCompactionModel(
  model: AnthropicModel,
): boolean {
  return (
    model === AnthropicModel.CLAUDE_4_6_OPUS ||
    model === AnthropicModel.CLAUDE_4_7_OPUS ||
    model === AnthropicModel.CLAUDE_NOUGAT_EAP ||
    model === AnthropicModel.CLAUDE_4_6_SONNET
  );
}

export const AnthropicToolChoiceLiteral = Union(
  // Literal("tool"), // TODO(@zkirby): Support "tool" tool choice option.
  Literal("any"),
  Literal("auto"),
  Literal("none"),
);
export type AnthropicToolChoice = Static<typeof AnthropicToolChoiceLiteral>;

export const DEFAULT_THINKING_BUDGET_TOKENS = 5000;
// The minimum allowed while still "being on" is 1024
// https://docs.aws.amazon.com/bedrock/latest/userguide/claude-messages-extended-thinking.html
export const DEFAULT_SMALL_THINKING_BUDGET_TOKENS = 1024;
export const DEFAULT_MAX_TOKENS = 20000;

const CacheControl = RRecord({
  type: Literal("ephemeral"),
});

export const AnthropicThinkingBlock = RRecord({
  type: Literal("thinking"),
  thinking: String,
  signature: String,
  cache_control: CacheControl.optional(),
});
export type AnthropicThinkingBlock = Static<typeof AnthropicThinkingBlock>;

export const RedactedAnthropicThinkingBlock = RRecord({
  type: Literal("redacted_thinking"),
  data: String, // field is 'data' not 'thinking' for redacted blocks
  signature: String,
  cache_control: CacheControl.optional(),
});
export type RedactedAnthropicThinkingBlock = Static<
  typeof RedactedAnthropicThinkingBlock
>;

export const AnthropicThinkingBlocks = Array(
  Union(AnthropicThinkingBlock, RedactedAnthropicThinkingBlock),
);
export type AnthropicThinkingBlocks = Static<typeof AnthropicThinkingBlocks>;

export const AnthropicThinkingConfig = RRecord({
  enabled: Boolean,
  budgetTokens: Number,
});
export type AnthropicThinkingConfig = Static<typeof AnthropicThinkingConfig>;

export const AnthropicTaskBudget = RRecord({
  type: Literal("tokens"),
  total: Number,
  remaining: Number.optional(),
});
export type AnthropicTaskBudget = Static<typeof AnthropicTaskBudget>;

export const AiEffortLevelLiteral = Union(
  Literal("low"),
  Literal("medium"),
  Literal("high"),
  Literal("max"),
);
export type AiEffortLevel = Static<typeof AiEffortLevelLiteral>;

/**
 * Config for Anthropic's tool search feature (beta header: advanced-tool-use-20
 * Each agent defines its own always-loaded tools in *_DEFAULT_ALWAYS_LOADED_TOOL_NAMES.
 * If tool search is enabled, other tools are deferred and loaded on demand.
 */
export const AnthropicToolSearchConfig = RRecord({
  enabled: Boolean,
  /** Tool names that should never be deferred (always loaded into context) */
  alwaysLoadedTools: Array(ToolNameLiteral).optional(),
});
export type AnthropicToolSearchConfig = Static<
  typeof AnthropicToolSearchConfig
>;

/**
 * Config for Anthropic server-side compaction context management edits.
 */
export const AnthropicCompactionConfig = RRecord({
  enabled: Boolean,
  trigger: RRecord({
    type: Literal("input_tokens"),
    value: Number,
  }).optional(),
  pauseAfterCompaction: Boolean.optional(),
  instructions: String.optional(),
});
export type AnthropicCompactionConfig = Static<
  typeof AnthropicCompactionConfig
>;

export const AnthropicModelParams = RRecord({
  model: AnthropicModelLiteral,
  maxTokens: Number,
  stopSequences: Array(String),
  temperature: Number.optional(),
  topK: Number.optional(),
  topP: Number.optional(),
  thinking: AnthropicThinkingConfig.optional(),
  effort: AiEffortLevelLiteral.optional(),
  taskBudget: AnthropicTaskBudget.optional(),
  toolSearch: AnthropicToolSearchConfig.optional(),
});
export type AnthropicModelParams = Static<typeof AnthropicModelParams>;

export const AnthropicMessageRoleLiteral = Union(
  Literal("user"),
  Literal("assistant"),
);
export type AnthropicMessageRole = Static<typeof AnthropicMessageRoleLiteral>;
export const AnthropicMessageRole = getNormalEnum(AnthropicMessageRoleLiteral);

export const TextBlock = RRecord({
  type: Literal("text"),
  text: String,
  cache_control: CacheControl.optional(),
});
export type TextBlock = Static<typeof TextBlock>;

export const AnthropicMediaType = Union(
  Literal("image/png"),
  Literal("image/jpeg"),
  Literal("image/webp"),
  Literal("image/gif"),
);
type AnthropicMediaType = Static<typeof AnthropicMediaType>;

const ImageBlock = RRecord({
  type: Literal("image"),
  source: RRecord({
    type: Literal("base64"),
    media_type: AnthropicMediaType,
    data: String,
  }),
  cache_control: CacheControl.optional(),
});
export type ImageBlock = Static<typeof ImageBlock>;

const DocumentBlock = RRecord({
  type: Literal("document"),
  source: RRecord({
    type: Literal("base64"),
    media_type: Literal("application/pdf"),
    data: String,
  }),
  cache_control: CacheControl.optional(),
});
export type DocumentBlock = Static<typeof DocumentBlock>;

export const ToolUseBlock = RRecord({
  type: Literal("tool_use"),
  id: String,
  name: String,
  input: Dictionary(Unknown, String),
  thinking: String.optional(),
  cache_control: CacheControl.optional(),
});
export type ToolUseBlock = Static<typeof ToolUseBlock>;

/**
 * Tool reference block for client-side tool search.
 * Used in tool_result content to reference deferred tools.
 *
 * @see https://docs.anthropic.com/en/docs/agents-and-tools/tool-use/tool-search-tool#custom-tool-search-implementation
 */
export const ToolReferenceBlock = RRecord({
  type: Literal("tool_reference"),
  tool_name: String,
  cache_control: CacheControl.optional(),
});
export type ToolReferenceBlock = Static<typeof ToolReferenceBlock>;

export const ToolResultBlock = RRecord({
  type: Literal("tool_result"),
  tool_use_id: String,
  content: Array(Union(TextBlock, ImageBlock, ToolReferenceBlock)),
  cache_control: CacheControl.optional(),
});
export type ToolResultBlock = Static<typeof ToolResultBlock>;

/**
 * Compaction content block returned by Anthropic server-side compaction.
 */
export const AnthropicCompactionBlock = RRecord({
  type: Literal("compaction"),
  content: String,
  cache_control: CacheControl.optional(),
});
export type AnthropicCompactionBlock = Static<typeof AnthropicCompactionBlock>;

export function isAnthropicCompactionBlock(
  block: unknown,
): block is AnthropicCompactionBlock {
  return AnthropicCompactionBlock.guard(block);
}

/**
 * Streaming delta for Anthropic compaction content.
 */
export const AnthropicCompactionDelta = RRecord({
  type: Literal("compaction_delta"),
  content: String,
});
export type AnthropicCompactionDelta = Static<typeof AnthropicCompactionDelta>;

export function isAnthropicCompactionDelta(
  delta: unknown,
): delta is AnthropicCompactionDelta {
  return AnthropicCompactionDelta.guard(delta);
}

export const AnthropicCompactionEdit = RRecord({
  type: Literal("compact_20260112"),
  trigger: RRecord({
    type: Literal("input_tokens"),
    value: Number,
  }).optional(),
  pause_after_compaction: Boolean.optional(),
  instructions: String.optional(),
});
export type AnthropicCompactionEdit = Static<typeof AnthropicCompactionEdit>;

export const AnthropicContextManagementWithCompaction = RRecord({
  edits: Array(AnthropicCompactionEdit),
});
export type AnthropicContextManagementWithCompaction = Static<
  typeof AnthropicContextManagementWithCompaction
>;

export const AnthropicMessage = RRecord({
  role: AnthropicMessageRoleLiteral,
  content: Array(
    Union(
      TextBlock,
      ToolUseBlock,
      ToolResultBlock,
      AnthropicCompactionBlock,
      ImageBlock,
      DocumentBlock,
      AnthropicThinkingBlock,
      RedactedAnthropicThinkingBlock,
    ),
  ),
});
export type AnthropicMessage = Static<typeof AnthropicMessage>;

export const AnthropicMessages = RRecord({
  systemMessage: String,
  userAndAssistantMessages: Array(AnthropicMessage),
});
export type AnthropicMessages = Static<typeof AnthropicMessages>;

export const AnthropicConformerResult = RRecord({
  messages: AnthropicMessages,
  totalContextTokens: Number.Or(Null),
  usedTokenCountApproximation: Boolean,
});
export type AnthropicConformerResult = Static<typeof AnthropicConformerResult>;

export const AnthropicErrorPayload = RRecord({
  type: Literal("error"),
  error: RRecord({
    type: String,
    message: String,
  }),
});
export type AnthropicErrorPayload = Static<typeof AnthropicErrorPayload>;
